import React from 'react';

// ============================================================
// Типы
// ============================================================

interface CareerItem {
  career: string;
  score: number;
  reasons: string[];
}

interface CareerPathsProps {
  careers: CareerItem[];
}

// ============================================================
// Константы
// ============================================================

const CAREER_CATEGORIES: Record<string, { label: string; icon: string; color: string; careers: string[] }> = {
  Tech: {
    label: 'Технологии',
    icon: '💻',
    color: '#2196F3',
    careers: ['IT-инженер', 'Программист', 'Data Scientist', 'UX-исследователь', 'QA-инженер', 'Аналитик данных'],
  },
  Creative: {
    label: 'Творчество',
    icon: '🎨',
    color: '#E91E63',
    careers: ['Художник', 'Музыкант', 'Актёр / Артист', 'Режиссёр', 'Продюсер', 'Фотограф', 'Кинематографист', 'Дизайнер', 'Стилист', 'Дизайнер интерьеров'],
  },
  Business: {
    label: 'Бизнес',
    icon: '💼',
    color: '#FF9800',
    careers: ['Предприниматель', 'CEO / Топ-менеджер', 'Руководитель', 'Менеджер проектов', 'Финансовый аналитик', 'Банкир', 'Бухгалтер', 'Event-менеджер', 'Строительный менеджер'],
  },
  Science: {
    label: 'Наука',
    icon: '🔬',
    color: '#9C27B0',
    careers: ['Исследователь', 'Астроном', 'Философ', 'Преподаватель ВУЗа', 'Историк', 'Геолог', 'Врач-диагност', 'Хирург', 'Педиатр', 'Фармацевт', 'Диетолог', 'Нутрициолог', 'Онколог', 'Криминалист'],
  },
  Social: {
    label: 'Социальная сфера',
    icon: '🤝',
    color: '#4CAF50',
    careers: ['Психолог', 'Психотерапевт', 'Социальный работник', 'Учитель', 'Преподаватель', 'HR-специалист', 'Юрист', 'Дипломат', 'Медиатор / Арбитр', 'Консультант', 'Международный консультант', 'Священнослужитель', 'Медитативный коуч'],
  },
  Active: {
    label: 'Активная деятельность',
    icon: '⚡',
    color: '#FF5722',
    careers: ['Военный / Полицейский', 'Спортсмен', 'Инженер', 'Архитектор', 'Моряк / Океанолог', 'Путешественник / Блогер', 'Журналист', 'Копирайтер', 'Переводчик', 'Маркетолог', 'Издатель', 'Государственный служащий', 'Финансовый аудитор', 'Разведчик / Аналитик спецслужб', 'Социальный активист', 'Инновационный предприниматель'],
  },
};

const COLORS = {
  bg: '#0a0814',
  cardBg: 'rgba(20, 18, 40, 0.85)',
  text: '#e0e0e0',
  textMuted: '#b0b0b0',
  blue: '#2196F3',
  orange: '#FF9800',
};

// ============================================================
// Хелпер: определить категорию профессии
// ============================================================

function getCareerCategory(careerName: string): string {
  for (const [key, cat] of Object.entries(CAREER_CATEGORIES)) {
    if (cat.careers.some(c => careerName.toLowerCase().includes(c.toLowerCase()) || c.toLowerCase().includes(careerName.toLowerCase()))) {
      return key;
    }
  }
  return 'Active'; // fallback
}

// ============================================================
// Компонент: Score Bar
// ============================================================

const ScoreBar: React.FC<{ score: number; color: string }> = ({ score, color }) => (
  <div style={{
    width: '100%',
    height: '8px',
    background: 'rgba(255,255,255,0.1)',
    borderRadius: '4px',
    overflow: 'hidden',
    marginTop: '8px',
  }}>
    <div style={{
      width: `${score}%`,
      height: '100%',
      background: color,
      borderRadius: '4px',
      transition: 'width 0.5s ease',
    }} />
  </div>
);

// ============================================================
// Компонент: Career Card
// ============================================================

const CareerCard: React.FC<{ career: CareerItem; index: number }> = ({ career, index }) => {
  const category = getCareerCategory(career.career);
  const catInfo = CAREER_CATEGORIES[category] || CAREER_CATEGORIES['Active'];

  return (
    <div style={{
      background: COLORS.cardBg,
      border: `1px solid ${catInfo.color}44`,
      borderRadius: '12px',
      padding: '16px',
      transition: 'transform 0.2s, box-shadow 0.2s',
    }}
    onMouseEnter={(e) => {
      e.currentTarget.style.transform = 'translateY(-2px)';
      e.currentTarget.style.boxShadow = `0 4px 20px ${catInfo.color}33`;
    }}
    onMouseLeave={(e) => {
      e.currentTarget.style.transform = 'translateY(0)';
      e.currentTarget.style.boxShadow = 'none';
    }}
    >
      {/* Заголовок */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
        <span style={{ fontSize: '24px' }}>{catInfo.icon}</span>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: '16px', fontWeight: 600, color: COLORS.text }}>
            {career.career}
          </div>
          <div style={{ fontSize: '12px', color: catInfo.color, fontWeight: 500 }}>
            {catInfo.label}
          </div>
        </div>
        <div style={{
          fontSize: '20px',
          fontWeight: 700,
          color: career.score >= 80 ? '#4CAF50' : career.score >= 60 ? COLORS.orange : COLORS.textMuted,
        }}>
          {career.score}
        </div>
      </div>

      {/* Score Bar */}
      <ScoreBar score={career.score} color={catInfo.color} />

      {/* Reasons */}
      {career.reasons.length > 0 && (
        <div style={{ marginTop: '12px', fontSize: '12px', color: COLORS.textMuted, lineHeight: 1.6 }}>
          {career.reasons.map((reason, i) => (
            <div key={i} style={{ marginBottom: '4px' }}>
              • {reason}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================================
// Основной компонент
// ============================================================

const CareerPaths: React.FC<CareerPathsProps> = ({ careers }) => {
  if (!careers || careers.length === 0) {
    return (
      <div style={{
        background: COLORS.cardBg,
        border: `1px solid ${COLORS.blue}33`,
        borderRadius: '12px',
        padding: '24px',
        textAlign: 'center',
        marginBottom: '24px',
        color: COLORS.textMuted,
      }}>
        <div style={{ fontSize: '32px', marginBottom: '8px' }}>🔮</div>
        <div>Рекомендуемые профессии пока не определены</div>
      </div>
    );
  }

  // Группируем по категориям
  const grouped: Record<string, CareerItem[]> = {};
  for (const career of careers) {
    const cat = getCareerCategory(career.career);
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(career);
  }

  return (
    <div style={{ marginBottom: '24px' }}>
      {/* Заголовок секции */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px',
      }}>
        <span style={{ fontSize: '28px' }}>🎯</span>
        <h2 style={{
          fontSize: '22px',
          fontWeight: 600,
          color: COLORS.blue,
          margin: 0,
        }}>
          Рекомендуемые профессии
        </h2>
      </div>

      {/* Карточки */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
        gap: '16px',
      }}>
        {careers.map((career, i) => (
          <CareerCard key={i} career={career} index={i} />
        ))}
      </div>

      {/* Легенда категорий */}
      <div style={{
        marginTop: '24px',
        padding: '16px',
        background: 'rgba(20, 18, 40, 0.6)',
        borderRadius: '8px',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
      }}>
        {Object.entries(CAREER_CATEGORIES).map(([key, cat]) => {
          // Показываем только те категории, которые есть в результатах
          if (!grouped[key]) return null;
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: COLORS.textMuted }}>
              <span>{cat.icon}</span>
              <span style={{ color: cat.color }}>{cat.label}</span>
              <span>({grouped[key].length})</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CareerPaths;
